import { supabase } from '@/lib/supabase';
import { getActiveDaysIfUnmanaged } from '@/lib/api/techActivity';
import { measureOrderBuild, summariseBuildTimes, type BuildTimeSummary } from '@/lib/stageTimeline';
import { fetchPausesForOrders } from '@/lib/api/pauses';
import { countedPauses, orderPauseIntervals, type PauseRow } from '@/lib/pauses';

/**
 * Typical build time for the orders completed in [from, to], measured exactly
 * as each order's detail page measures it (see measureOrderBuild) and
 * summarised as a median (see summariseBuildTimes).
 *
 * `scope` narrows the order query to the technicians the caller may see; it
 * receives and returns the Supabase query builder.
 */
export async function fetchBuildTimeSummary(
  scope: (query: any) => any,
  from: string,
  to: string,
): Promise<BuildTimeSummary | null> {
  const { data: orders, error } = await scope(
    supabase
      .from('assembly_orders')
      .select('id, technician_id, quantity, created_at, started_at, tech_signed_at, completed_at, completed_date, stage_timestamps')
      .not('completed_at', 'is', null)
      .gte('completed_date', from)
      .lte('completed_date', to),
  );
  if (error || !orders?.length) return null;

  // Earliest stress test per order. A missing table grant degrades to "no
  // stress evidence" rather than failing the whole stat.
  const stressByOrder = new Map<string, string>();
  const ids: string[] = orders.map((o: any) => o.id);
  for (let i = 0; i < ids.length; i += 100) {
    const { data: tests } = await supabase
      .from('assembly_order_stress_tests')
      .select('assembly_order_id, started_at')
      .in('assembly_order_id', ids.slice(i, i + 100))
      .not('started_at', 'is', null);
    for (const t of tests ?? []) {
      const cur = stressByOrder.get(t.assembly_order_id);
      if (!cur || new Date(t.started_at) < new Date(cur)) stressByOrder.set(t.assembly_order_id, t.started_at);
    }
  }

  // Active days for unmanaged techs, from each tech's earliest order creation:
  // a build can start well before the range it was completed in.
  const activeDaysByTech = new Map<string, Set<string> | undefined>();
  const techIds = [...new Set(orders.map((o: any) => o.technician_id).filter(Boolean))] as string[];
  await Promise.all(techIds.map(async tid => {
    const earliest = orders
      .filter((o: any) => o.technician_id === tid)
      .map((o: any) => o.created_at as string)
      .reduce((a: string, b: string) => (new Date(a) < new Date(b) ? a : b));
    activeDaysByTech.set(tid, await getActiveDaysIfUnmanaged(tid, new Date(earliest), new Date()));
  }));

  // Paused time comes off each build, as on its detail page. Like the stress
  // tests, an unreadable pause table degrades to "no pauses".
  const pausesByOrder = await fetchPausesForOrders(ids).catch(() => new Map<string, PauseRow[]>());

  return summariseBuildTimes(orders.map((o: any) =>
    measureOrderBuild(
      o,
      stressByOrder.get(o.id),
      o.technician_id ? activeDaysByTech.get(o.technician_id) : undefined,
      orderPauseIntervals(countedPauses(pausesByOrder.get(o.id) ?? []), o.quantity || 1),
    ),
  ));
}
